import { reactive, computed } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import { usePersona } from '@/composables/usePersona';

const POLLING_INTERVAL_MS = 2000;
const POLLING_TIMEOUT_MS = 10 * 60 * 1000;

export function useOwnership(props, emit) {
  const store = useStore();
  const toast = useToast();
  const persona = usePersona();

  const state = reactive({
    enableModal: false,
    displayIdvModal: false,
    loading: false,
    percentage: null,
    jobTitle: null,
    attestation: null,
  });

  const personaEvent = computed(() => persona.event.value);
  const ownershipData = computed(() => store.state.ownership.ownershipData);
  const currentUser = computed(() => store.state.users.currentUser);
  const userSelectedLoadPayOrDelta = computed(() => {
    const data = store.state.carriers.carrierData;
    return (
      data?.payment_method?.is_type == 'load_pay' ||
      data?.payment_term?.is_type == 'delta'
    );
  });

  const existingOwnershipUser = computed(() => {
    return (
      ownershipData?.value.ownership_users?.find(
        (user) => user.user_id === currentUser.value.id
      ) ?? null
    );
  });

  function toggleModal() {
    state.enableModal = !state.enableModal;
  }

  function closeModal() {
    state.enableModal = false;
    emit('close');
  }

  function getFormattedTimestamp() {
    const now = new Date();
    return (
      now.getFullYear() +
      '-' +
      String(now.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(now.getDate()).padStart(2, '0') +
      ' ' +
      String(now.getHours()).padStart(2, '0') +
      ':' +
      String(now.getMinutes()).padStart(2, '0') +
      ':' +
      String(now.getSeconds()).padStart(2, '0') +
      '.' +
      String(now.getMilliseconds()).padStart(6, '0')
    );
  }

  function showErrorToast(error) {
    toast?.open({
      message:
        error?.response?.data?.[0] ||
        'Something went wrong with your request. Please try again',
      position: 'top-right',
      duration: 5000,
    });
  }

  async function ensureOwnershipCompanyExists() {
    const companyId = store.state.users.currentUser.company.id;
    const ownership = await store.dispatch(
      'ownership/getOwnershipByCompanyId',
      companyId
    );
    if (!ownership) {
      await store.dispatch('ownership/createOwnershipCompany', { companyId });
    }
  }

  async function initializePersonaFlow() {
    state.displayIdvModal = true;

    const getInquiry = await store.dispatch('users/getSSNInquiry');
    persona.initializePersonaClientWithInquiry(
      getInquiry?.data?.external_id,
      getInquiry?.data?.session_token
    );
  }

  async function claimControllingManager(attestation) {
    try {
      const ownershipUsers = ownershipData?.value.ownership_users || [];

      const registeredOwner = ownershipUsers.find(
        (user) => user.user_id === currentUser.value.id
      );

      let ownershipUserId;

      if (registeredOwner) {
        await store.dispatch('ownership/changeControllingManager', {
          ownership_id: registeredOwner.ownership_id,
          user_id: currentUser.value.id,
        });
        ownershipUserId = registeredOwner.id;
      } else {
        if (!ownershipData.value) await ensureOwnershipCompanyExists();
        const { id: ownershipId } = store.state.ownership.ownershipData || {};
        const response = await store.dispatch('ownership/createOwnershipUser', {
          ownershipId,
          email: currentUser.value.email,
          type: 'controlling_manager',
          jobTitle: state.jobTitle,
          percentage: state.percentage,
          details_confirmed_at: getFormattedTimestamp(),
        });
        ownershipUserId = response?.data?.id;
      }

      if (attestation && ownershipUserId)
        store.dispatch('ownership/recordCertification', ownershipUserId);
    } catch (error) {
      showErrorToast(error);
    }
  }

  async function updateUser() {
    const {
      users: { currentUser },
    } = store.state;

    const ownershipId = existingOwnershipUser.value?.id;
    const companyId = currentUser.company.id;

    const baseBody = {
      ownership_user: {
        details_confirmed_at: getFormattedTimestamp(),
        ownership_percentage: state.percentage,
        job_title: state.jobTitle,
      },
    };

    try {
      await store.dispatch('ownership/UpdateOwnershipUser', {
        ownershipId,
        body: baseBody,
      });

      await store.dispatch('ownership/getOwnershipByCompanyId', companyId);

      toast?.open({
        message: 'Ownership details updated successfully',
        position: 'top-right',
        duration: 5000,
      });
    } catch (error) {
      showErrorToast(error);
    } finally {
      cleanUpState();
    }
  }

  const cleanUpState = () => {
    Object.assign(state, {
      loading: false,
      displayIdvModal: false,
      percentage: null,
      jobTitle: null,
    });
  };

  async function checkUserOwnership() {
    state.loading = true;
    let elapsedTime = 0;
    let isConfirmed = false;

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    while (elapsedTime < POLLING_TIMEOUT_MS) {
      const own = await store.dispatch(
        'ownership/getOwnershipByCompanyId',
        store.state.users.currentUser.company.id
      );

      isConfirmed = Boolean(
        own?.data.ownership_users?.some(
          (user) =>
            user.user_id === store.state.users.currentUser.id &&
            user.ssn_submitted_at != null
        )
      );

      if (isConfirmed) break;
      await sleep(POLLING_INTERVAL_MS);
      elapsedTime += POLLING_INTERVAL_MS;
    }

    if (isConfirmed) {
      if (props.assignAsControllingManager) {
        cleanUpState();
        emit('execNextStep');
      } else {
        await updateUser();
      }
    }
  }

  async function handleConfirmOwnership({ percentage, jobTitle, attestation }) {
    state.loading = true;
    let ownershipUserId = null;

    try {
      await ensureOwnershipCompanyExists();
      state.percentage = percentage;
      state.jobTitle = jobTitle;

      if (props.assignAsControllingManager) {
        await claimControllingManager(attestation);
      } else {
        if (!existingOwnershipUser.value) {
          const response = await store.dispatch(
            'ownership/createOwnershipUser',
            {
              ownershipId: ownershipData.value.id,
              email: currentUser.value.email,
              type: 'owner',
            }
          );
          ownershipUserId = response?.data?.id;
        }

        if (!ownershipUserId) ownershipUserId = existingOwnershipUser.value?.id;

        if (attestation && ownershipUserId)
          store.dispatch('ownership/recordCertification', ownershipUserId);
      }

      const isOwnershipStep = window.location.hash === '#ownership-1';

      if (!isOwnershipStep || userSelectedLoadPayOrDelta.value) {
        state.displayIdvModal = true;
        await initializePersonaFlow();
      } else {
        await ensureOwnershipCompanyExists();

        updateUser();
      }
    } catch (error) {
      showErrorToast(error);
    } finally {
      state.loading = false;
    }
  }

  return {
    state,
    personaEvent,
    toggleModal,
    closeModal,
    handleConfirmOwnership,
    checkUserOwnership,
  };
}
